import React, { useState, Children, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowRight, Heart, Coffee, BookOpen, DollarSign, Star } from 'lucide-react';
// Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-2">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-4" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-5">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-6">
              <h2 className="text-2xl font-bold text-[#1E3A5F]" data-id="element-7">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close modal" data-id="element-8">
                <X className="w-5 h-5 text-gray-500" data-id="element-9" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] text-gray-700 leading-relaxed" data-id="element-10">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// Theme Colors
const colors = {
  bg: 'bg-[#FDF8F3]',
  bgAlt: 'bg-[#F5EDE4]',
  navy: 'text-[#1E3A5F]',
  gold: 'bg-[#D4A853]',
  goldText: 'text-[#D4A853]',
  sage: 'bg-[#E8F0EB]',
  text: 'text-gray-700',
  heading: 'text-[#1E3A5F]'
};
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className={`min-h-screen ${colors.bg} overflow-x-hidden`} data-id="element-11">
      {/* Hero Section */}
      <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32" data-id="element-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-13">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-14">
            <motion.div variants={fadeInUp} className="space-y-4 text-lg md:text-xl text-gray-500 font-medium italic" data-id="element-15">
              <p className="line-through decoration-red-400 decoration-2" data-id="element-16">
                You don't need to stand for hours.
              </p>
              <p className="line-through decoration-red-400 decoration-2" data-id="element-17">
                You don't need to ship anything.
              </p>
              <p className="line-through decoration-red-400 decoration-2" data-id="element-18">
                You don't need to be on camera.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-19">
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold ${colors.heading} leading-tight mb-4`} data-id="element-20">
                Couch Cash <br data-id="element-21" />
                <span className="italic font-normal text-[#D4A853]" data-id="element-22">
                  for Grandmas
                </span>
              </h1>
              <p className={`text-xl md:text-2xl ${colors.text} leading-relaxed`} data-id="element-23">
                The 7-Day Guide to Make & Sell Printable Recipe Cards{' '}
                <span className="whitespace-nowrap" data-id="element-24">(From the Couch)</span>
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="p-6 bg-white/60 border-l-4 border-[#D4A853] rounded-r-lg backdrop-blur-sm" data-id="element-25">
              <p className="text-lg text-gray-600" data-id="element-26">
                <span className="font-bold text-[#1E3A5F]" data-id="element-27">Note:</span> This is{' '}
                <strong data-id="element-28">not</strong> a cookbook. You are <strong data-id="element-29">not</strong>{' '}
                buying pre-made recipe cards to cook with. You're learning to
                build a simple digital product.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-30">
              <button onClick={scrollToPricing} className={`${colors.gold} hover:bg-[#c29642] text-white text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center gap-3`} data-id="element-31">
                Start Your 7-Day Journey <ArrowRight className="w-6 h-6" data-id="element-32" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-33">
            <div className="absolute inset-0 bg-[#D4A853]/10 rounded-3xl transform rotate-3 scale-105 z-0" data-id="element-34"></div>
            <img src="https://cdn.magicpatterns.com/uploads/mB3HbJVidKYTWQBwNcz9WK/grandma_mockup.png" alt="Grandma holding a tablet showing the Couch Cash guide" className="relative z-10 rounded-2xl shadow-2xl w-full object-cover" data-id="element-35" />
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-36">
        <div className="max-w-3xl mx-auto text-center" data-id="element-37">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-38">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-39">
              Because here's the truth:
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-12" data-id="element-40">
              A lot of grandmas already have something valuable. You've
              collected recipes for years. You know what people actually like.
              You know how to make something feel "home."
            </p>

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm text-left relative overflow-hidden" data-id="element-41">
              <div className="absolute top-0 left-0 w-full h-2 bg-[#1E3A5F]" data-id="element-42"></div>
              <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6" data-id="element-43">
                But turning that into something you can sell online?
              </h3>
              <p className="text-lg text-gray-600 mb-6" data-id="element-44">
                That's where most people get stuck. Not because they're lazy.
                But because the internet makes it feel like you need:
              </p>
              <ul className="space-y-4" data-id="element-45">
                {['12 different apps to learn', 'Perfect branding and logos', "Hours of energy you don't have", 'A loud personality on social media'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-gray-500" data-id="element-46">
                    <X className="w-6 h-6 text-red-400 flex-shrink-0" data-id="element-47" />
                    <span className="line-through decoration-gray-300" data-id="element-48">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-8 text-lg font-medium text-[#1E3A5F] bg-[#E8F0EB] p-4 rounded-lg" data-id="element-49">
                If you're dealing with chronic pain or low energy, that kind of
                advice isn't just unhelpful—it's impossible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section: 1-1-1 Method */}
      <section className="py-20 px-4" data-id="element-50">
        <div className="max-w-4xl mx-auto" data-id="element-51">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-52">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-6`} data-id="element-53">
              This guide is built for grandmas who need it simple.
            </h2>
            <p className="text-xl text-gray-600" data-id="element-54">
              Inside{' '}
              <span className="font-bold text-[#D4A853]" data-id="element-55">
                Couch Cash for Grandmas
              </span>
              , you follow one calm system:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 text-center" data-id="element-56">
            {[{
            icon: Heart,
            title: 'Pick 1 Theme',
            desc: 'Choose a recipe style you already know and love.'
          }, {
            icon: BookOpen,
            title: 'Make 1 Pack',
            desc: 'Create a simple printable set using our template.'
          }, {
            icon: DollarSign,
            title: 'Post in 1 Place',
            desc: "List it on Etsy. That's it. No complicated websites."
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="bg-white p-8 rounded-2xl shadow-md border-b-4 border-[#D4A853]" data-id="element-57">
                <div className="w-16 h-16 bg-[#FDF8F3] rounded-full flex items-center justify-center mx-auto mb-6 text-[#D4A853]" data-id="element-58">
                  <step.icon className="w-8 h-8" data-id="element-59" />
                </div>
                <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3" data-id="element-60">
                  {step.title}
                </h3>
                <p className="text-gray-600" data-id="element-61">{step.desc}</p>
              </motion.div>)}
          </div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mt-12 text-lg text-gray-500 italic" data-id="element-62">
            No bouncing between platforms. No trying to learn everything before
            you start. <br data-id="element-63" />
            No "someday when I feel better."
          </motion.p>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-64">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-65">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-66">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-67">
              What you'll have at the end of 7 days
            </h2>
            <ul className="space-y-6" data-id="element-68">
              {['A finished printable recipe card set (your first real digital product)', 'A properly saved PDF file ready for buyers to download', 'A complete Etsy listing (with fill-in-the-blank words)', 'Simple post scripts you can copy to share it (no camera required)'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-69">
                  <div className="bg-[#D4A853] rounded-full p-1 mt-1 flex-shrink-0" data-id="element-70">
                    <Check className="w-4 h-4 text-white" data-id="element-71" />
                  </div>
                  <span className="text-lg md:text-xl text-gray-700" data-id="element-72">
                    {item}
                  </span>
                </li>)}
            </ul>
            <p className="mt-8 text-lg font-medium text-[#1E3A5F]" data-id="element-73">
              It's not theory. It's "do this, then this, then this."
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl shadow-lg rotate-2 border border-gray-100" data-id="element-74">
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center h-full min-h-[300px]" data-id="element-75">
              <Star className="w-12 h-12 text-[#D4A853] mb-4" data-id="element-76" />
              <h3 className="text-2xl font-serif text-[#1E3A5F] mb-2" data-id="element-77">
                Grandma's Secret Cookie Recipes
              </h3>
              <p className="text-gray-500 mb-6" data-id="element-78">
                Printable Recipe Cards • Instant Download
              </p>
              <div className="w-full h-32 bg-[#FDF8F3] rounded-lg mb-4 flex items-center justify-center text-gray-400 text-sm" data-id="element-79">
                [Your Beautiful Product Here]
              </div>
              <span className="bg-[#E8F0EB] text-[#1E3A5F] px-4 py-1 rounded-full text-sm font-bold" data-id="element-80">
                Ready to Sell
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-20 px-4" data-id="element-81">
        <div className="max-w-4xl mx-auto" data-id="element-82">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-83">
            <div className="bg-[#1E3A5F] p-8 md:p-10 text-center" data-id="element-84">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-85">
                What's inside the ebook
              </h2>
              <p className="text-[#D4A853] text-lg font-medium" data-id="element-86">
                A grandma-friendly 7-day plan
              </p>
            </div>

            <div className="p-8 md:p-12 grid md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-87">
              {['How to choose a recipe card theme people search for', 'How to make cards using a simple drag-and-drop template', 'How to use LARGE PRINT for a premium, readable look', 'How to save your files the right way for clean printing', 'How to price your pack without undercharging', 'Step-by-step Etsy listing instructions', 'Copy/paste listing description (edit in minutes)', 'Tag ideas that make sense (no confusing SEO talk)', 'Simple social post templates (no face required)', "A 'bad pain day' plan for 5-minute progress"].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-88">
                  <div className="w-2 h-2 rounded-full bg-[#D4A853] mt-2.5 flex-shrink-0" data-id="element-89"></div>
                  <span className="text-lg text-gray-700" data-id="element-90">{item}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who it's for / Not for */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-91">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-92">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-[#8DA399]" data-id="element-93">
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6 flex items-center gap-3" data-id="element-94">
              <Check className="w-6 h-6 text-[#8DA399]" data-id="element-95" />
              This is for you if:
            </h3>
            <ul className="space-y-4" data-id="element-96">
              {["You're a grandma (50+) and want extra income", "You're on the couch/bed a lot (pain, low energy, recovery)", 'You want something simple you can finish', 'You want to sell a digital product (no shipping, no inventory)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-gray-700" data-id="element-97">
                  <span className="text-[#8DA399] font-bold" data-id="element-98">•</span>
                  {item}
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-gray-300" data-id="element-99">
            <h3 className="text-2xl font-bold text-gray-600 mb-6 flex items-center gap-3" data-id="element-100">
              <X className="w-6 h-6 text-gray-400" data-id="element-101" />
              This is NOT for you if:
            </h3>
            <ul className="space-y-4" data-id="element-102">
              {['You want money without doing anything', 'You refuse to do any digital steps at all', 'You only want to sell physical products that require shipping'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-gray-500" data-id="element-103">
                  <span className="text-gray-400 font-bold" data-id="element-104">•</span>
                  {item}
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Why Recipe Cards */}
      <section className="py-20 px-4" data-id="element-105">
        <div className="max-w-3xl mx-auto text-center" data-id="element-106">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-107">
            <div className="inline-block p-3 bg-[#FDF8F3] rounded-full mb-6" data-id="element-108">
              <Coffee className="w-8 h-8 text-[#D4A853]" data-id="element-109" />
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-110">
              Why recipe cards?
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-111">
              Because recipe cards are one of the easiest "first products" for
              grandmas:
            </p>

            <div className="grid sm:grid-cols-2 gap-6 text-left mb-12" data-id="element-112">
              {[{
              title: 'Familiar',
              desc: "Doesn't feel techy or scary"
            }, {
              title: 'Quick',
              desc: 'Make in small sessions'
            }, {
              title: 'Scalable',
              desc: 'Easy to bundle later for more income'
            }, {
              title: 'In Demand',
              desc: 'Buyers love printable kitchen tools'
            }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-113">
                  <h4 className="font-bold text-[#1E3A5F] text-lg mb-1" data-id="element-114">
                    {item.title}
                  </h4>
                  <p className="text-gray-600" data-id="element-115">{item.desc}</p>
                </div>)}
            </div>

            <p className="text-lg font-medium text-[#1E3A5F] italic" data-id="element-116">
              You're not trying to become an influencer. <br data-id="element-117" />
              You're creating something clear, useful, and sellable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="pricing" className={`py-24 px-4 bg-[#1E3A5F] text-white text-center`} data-id="element-118">
        <div className="max-w-4xl mx-auto" data-id="element-119">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-120">
            <h2 className="text-3xl md:text-5xl font-bold font-serif mb-6" data-id="element-121">
              Ready to make something you can sell from the couch?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-122">
              If you want a simple plan you can follow—without being
              overwhelmed—this guide was made for you.
            </p>

            <div className="bg-[#0f2340] p-8 rounded-3xl border border-white/10 max-w-lg mx-auto mb-10" data-id="element-123">
              <h3 className="text-2xl font-bold text-[#D4A853] mb-2" data-id="element-124">
                Couch Cash for Grandmas
              </h3>
              <p className="text-gray-300 mb-4" data-id="element-125">The Complete 7-Day Guide</p>

              <div className="mb-8" data-id="element-126">
                <div className="flex items-center justify-center gap-3" data-id="element-127">
                  <span className="text-5xl md:text-6xl font-bold text-white" style={{
                  fontFamily: 'Inter, system-ui, sans-serif'
                }} data-id="element-128">
                    $6
                    <span className="text-3xl md:text-4xl align-top" data-id="element-129">.49</span>
                  </span>
                  <span className="text-lg text-gray-400 line-through" data-id="element-130">
                    $19.99
                  </span>
                </div>
                <span className="inline-block mt-2 bg-[#D4A853]/20 text-[#D4A853] px-3 py-1 rounded-full text-sm font-semibold" data-id="element-131">
                  68% OFF
                </span>
              </div>

              <a href="https://www.mellowreset.com/cart/42953644408898:1" className="block w-full bg-[#D4A853] hover:bg-[#c29642] text-white text-xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-4 text-center" data-id="element-132">
                Start Now
              </a>
              <p className="text-sm text-gray-400" data-id="element-133">
                Instant Digital Download • Secure Checkout
              </p>
            </div>

            <p className="text-2xl font-serif italic text-[#D4A853]" data-id="element-134">
              "You don't need perfect. You need posted."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#1a3354]" data-id="element-135">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-136">
          <p className="text-gray-400 text-sm" data-id="element-137">
            © {new Date().getFullYear()} Couch Cash for Grandmas. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-138">
            <button onClick={() => setPrivacyOpen(true)} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-139">
              Privacy Policy
            </button>
            <button onClick={() => setTermsOpen(true)} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-140">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-141">
        <div className="space-y-4" data-id="element-142">
          <p className="text-sm text-gray-500" data-id="element-143">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-144">
            Information We Collect
          </h3>
          <p data-id="element-145">
            When you purchase our digital products, we collect only the
            information necessary to process your order, including your email
            address and payment information. Payment processing is handled
            securely by third-party providers.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-146">
            How We Use Your Information
          </h3>
          <p data-id="element-147">
            We use your information solely to deliver your purchased digital
            products and to send important updates about your purchase. We do
            not sell, rent, or share your personal information with third
            parties for marketing purposes.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-148">
            Digital Product Delivery
          </h3>
          <p data-id="element-149">
            Our products are delivered digitally. Once your purchase is
            complete, you will receive access to download your files. No
            physical products are shipped.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-150">Cookies</h3>
          <p data-id="element-151">
            Our website may use cookies to improve your browsing experience. You
            can choose to disable cookies in your browser settings.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-152">Your Rights</h3>
          <p data-id="element-153">
            You have the right to access, correct, or delete your personal
            information. You may also opt out of marketing communications at any
            time.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-154">
            Changes to This Policy
          </h3>
          <p data-id="element-155">
            We may update this privacy policy from time to time. Any changes
            will be posted on this page with an updated revision date.
          </p>
        </div>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-156">
        <div className="space-y-4" data-id="element-157">
          <p className="text-sm text-gray-500" data-id="element-158">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-159">
            Digital Product Purchase
          </h3>
          <p data-id="element-160">
            By purchasing "Couch Cash for Grandmas," you are buying a digital
            ebook guide. This is an educational product designed to help you
            learn how to create and sell printable recipe cards.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-161">
            What You're Getting
          </h3>
          <p data-id="element-162">
            This product is a step-by-step instructional guide. It does not
            include pre-made recipe cards, templates for resale, or any physical
            products. Results depend on your own effort and implementation.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-163">No Guarantees</h3>
          <p data-id="element-164">
            While we provide actionable guidance, we cannot guarantee any
            specific income or results. Success depends on many factors
            including your effort, market conditions, and execution.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-165">Refund Policy</h3>
          <p data-id="element-166">
            Due to the digital nature of this product, all sales are final. Once
            you receive access to download the ebook, refunds are not available.
            Please read the product description carefully before purchasing.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-167">
            Intellectual Property
          </h3>
          <p data-id="element-168">
            This ebook is for your personal use only. You may not resell,
            redistribute, or share the content with others. The content is
            protected by copyright.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-169">
            Limitation of Liability
          </h3>
          <p data-id="element-170">
            We are not liable for any damages or losses resulting from your use
            of this product or your business activities. This guide is for
            educational purposes only.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-171">Agreement</h3>
          <p data-id="element-172">
            By purchasing this product, you agree to these terms of service.
          </p>
        </div>
      </Modal>
    </div>;
}